// src\app\components\CheatSheetPdf_Person.js

import pdfMake from "pdfmake/build/pdfmake.js"
import pdfFonts from "pdfmake/build/vfs_fonts.js"
import nLogo from "../assets/images/numerable-logo-2022-512w-8b.png"
import cLogo from "../assets/images/the350club-logo-2024-8b.png"
import HOOK from "./hooks"
import { CHECKBOX } from "../modules/checkbox_constants"

import { BASE, COLOR, COLUMN, FORMATTING, JARGON, KIND, RADIO, TABLE_BIT, TENSE, TENSE_ENUM } from "../modules/constants"
import { getCapacities, getNameProp } from "../modules/utilities"
import { docDefinition, footer, get, getHeader, header, infoTable, initTableLayouts, rolesLabel, sortArray, styles, width } from "./CheatSheetPdf_Common"
import { detailsTable, titleTable } from "./CheatSheetPdf_Common"

pdfMake.vfs = pdfFonts

const { right } = FORMATTING
const { greyText, veryDarkGreyText } = COLOR.PDF
const tableLayouts = {}

const cheatSheetPdf_PersonBoardRoles = props => {
  // console.log("cheatSheetPdf_PersonBoard props:", props)
  initTableLayouts(props.brandColor, tableLayouts)
  const document = createDocument({ ...props, tableLayouts })
  pdfMake.createPdf(document).open()
}

const createDocument = props => ({
  ...docDefinition,
  styles: styles(props),
  header: header(props),
  content: getContent(props),
  images: {
    numerableLogo: nLogo,
    the350ClubLogo: cLogo,
  },
  footer: (currentPage, pageCount) => footer(currentPage, pageCount),
})

const getContent = props => {
  // console.log("getContent props: ", props)
  props.untilColumn = !!(props.orderSet & TABLE_BIT.RESIGNED)
  const content = [
    titleTable(props),
    detailsTable(props),
    rolesLabel({ ...props, tableLayouts }),
  ]

  const nodes = props.nodes.filter(i => i.level > 0) // all but the hub node

  getTenseContent({ ...props, content, nodes, tenseEnum: TENSE_ENUM.CURRENT })
  getTenseContent({ ...props, content, nodes, tenseEnum: TENSE_ENUM.NOTICE })
  getTenseContent({ ...props, content, nodes, tenseEnum: TENSE_ENUM.FUTURE })
  getTenseContent({ ...props, content, nodes, tenseEnum: TENSE_ENUM.PAST })

  content.push(infoTable({ ...props, tableLayouts }))

  return content
}

const getTenseContent = props => {
  const { content, tenseEnum } = props
  if (!!(CHECKBOX[HOOK.TENSE][tenseEnum].BIT & props.checkboxes[HOOK.TENSE]))
    content.push(props.radio[BASE.PERSON] === RADIO[BASE.PERSON].CONNECTIONS
      ? connectionsTable(bodyTableProps(props, tenseEnum))
      : appointmentsTable(bodyTableProps(props, tenseEnum))
    )
}

const bodyTableProps = (props, tenseEnum) => ({
  ...props,
  tenseBits: (CHECKBOX[HOOK.TENSE][tenseEnum].BIT & props.checkboxes[HOOK.TENSE]),
  tenseEnum,
})

//#region appointments table
const appointmentsTable = props => {
  // console.log('appointmentsTable props:', props)
  const body = getAppointments(props)

  return body
    ? ({
      style: "bodyTable",
      layout: tableLayouts.body,
      table: {
        widths: props.maxLseData
          ? props.untilColumn
            ? ["*", width.role, width.index, width.date, width.date, width.tenure, width.remuneration]
            : ["*", width.role, width.index, width.date, width.tenure, width.remuneration]
          : props.untilColumn
            ? ["*", width.role, width.date, width.date, width.tenure]
            : ["*", width.role, width.date, width.tenure],
        headerRows: 1,
        body,
      },
    })
    : null
}

const getAppointmentsHeaders = ({ props, orderSet, tenseEnum }) => {
  const style = "tableHeader"
  const alignment = right
  const name = getHeader({ sortId: COLUMN.COMPANY, style })
  const role = getHeader({ sortId: COLUMN.ROLE, prefix: `${TENSE[tenseEnum].NAME} ` })
  const index = getHeader({ sortId: COLUMN.LSE_INDEX, prefix: `${JARGON.LSE} `, style })
  const appointed = getHeader({ sortId: COLUMN.FROM, style, alignment })
  const resigned = getHeader({ sortId: COLUMN.UNTIL, style, alignment })
  const tenure = getHeader({ sortId: COLUMN.TENURE, style, alignment })
  const remuneration = getHeader({ sortId: COLUMN.COMPENSATION, style, alignment })

  const array = [
    name,
    role
  ]

  if (props.maxLseData)
    array.push(index)

  array.push(appointed)

  return props.maxLseData
    ? !!(orderSet & TABLE_BIT.RESIGNED)
      ? [...array, resigned, tenure, remuneration]
      : [...array, tenure, remuneration]
    : !!(orderSet & TABLE_BIT.RESIGNED)
      ? [...array, resigned, tenure]
      : [...array, tenure]
}

const getAppointments = props => {
  // console.log("getAppointments props:", props)
  const { connections, checkboxes, features, hubData, orderSet, tableOrder, tenseEnum, via } = props
  const nodes = props.nodes.filter(i => !!(checkboxes[HOOK.TENSE] & i.tenseBits))
  const array = []
  nodes.forEach(node => {
    // Get all connections where parentId === node.id
    // NB: The connections have already been filtered by childId === hubNode id
    const { entity } = node
    const indexBit = get.indexBit(node)
    // Handle legacy entity structure
    const { name } = entity.commonProps ? entity.commonProps : entity

    const connection = Object.filter(connections, c => c.parentId === node.id)
    const capacities = getCapacities(connection, node.id)
    const officerCapacities = (capacities && capacities.officer) || []
    const rolesByTense = officerCapacities.filter(c => c.tense === tenseEnum)
    const bold = indexBit ? true : null

    rolesByTense.forEach(role => {
      // Add the columns which are present regardless of tense
      const row = [
        { text: getNameProp.toPresent(name), color: veryDarkGreyText, sortValue: getNameProp.toSort(name), bold },
        {
          text:
            features.EnablePosition && role.position
              ? role.position.title
              : role.roleDescription
                ? role.roleDescription : "Officer",
          color: greyText,
          sortValue: get.positionSortValue(role.position.title),
          bold
        }]

      // Add the lse index, aligned left
      if (props.maxLseData)
        row.push({ text: getIndexText(indexBit), color: greyText, sortValue: get.indexSortValue(indexBit), bold })

      row.push({ text: get.appointedToBoard(role.from, right), color: greyText, sortValue: get.fromSortValue(role.from), bold })

      // Add the until (toDate) if tense past or outgoing
      if (props.untilColumn)
        row.push({ text: get.resignedFromBoard(hubData.toDate, role, right), color: greyText, sortValue: get.toDateSortValue(role.until), bold })

      // Add the remaining columns
      row.push({ text: get.yearsAndMonths(role.from, role.until, right), color: greyText, sortValue: role.days, bold })
      if (props.maxLseData)
        row.push({ text: get.remunerationObj(role.compensationGBP, right), color: greyText, sortValue: get.compensationSortValue(role.compensationGBP), bold })

      array.push(row)
    })
  })

  // console.log('array:', array)
  if (array.length) {
    const headers = getAppointmentsHeaders({ props, orderSet, tenseEnum })
    sortArray({ array, headers, orderSet, tableOrder, kind: KIND.NEXUS.PERSON.ROLES })
    return [headers, ...array]
  } else
    return null
}
//#endregion appointments table

//#region connections table
const connectionsTable = props => {
  // console.log('appointmentsTable props:', props)
  const body = getConnections(props)

  return body
    ? ({
      style: "bodyTable",
      layout: tableLayouts.body,
      table: {
        widths: props.maxLseData
          ? props.untilColumn
            ? ["25%", "*", width.index, width.date, width.date, width.overlap]
            : ["25%", "*", width.index, width.date, width.overlap]
          : props.untilColumn
            ? ["25%", "*", width.date, width.date, width.overlap]
            : ["25%", "*", width.date, width.overlap],
        headerRows: 1,
        body,
      },
    })
    : null
}

const getConnections = props => {
  // console.log("getConnections props:", props)
  const { maxLseData, orderSet, tableOrder, tenseEnum } = props
  const nodes = props.nodes.filter(i => i.tense === tenseEnum).filter(i => i.level === 2)
  const array = []
  nodes.forEach(node => {
    // console.log('node:', node)
    const { entity, connectionOverlapDuration, connectionOverlapEnd, connectionOverlapStart, via } = node
    const indexBit = get.indexBit(node)
    // Handle legacy entity structure
    const { name } = entity.commonProps ?? entity

    const bold = indexBit ? true : null
    const color = veryDarkGreyText

    const row = []
    row.push({ text: getNameProp.toPresent(name), color, sortValue: getNameProp.toSort(name), bold })
    row.push({ text: getNameProp.toPresent(via?.name), color, sortValue: getNameProp.toSort(via?.name), bold })

    if (maxLseData)
      row.push({ text: getIndexText(indexBit), color: greyText, sortValue: get.indexSortValue(indexBit), bold })

    row.push({ text: get.appointedToBoard(node.connectionStart, right), color, sortValue: get.fromSortValue(node.connectionStart), })

    if (props.untilColumn)
      row.push({
        text: get.resignedFromBoard(node.connectionEnd, { tense: node.tense }, right),
        color,
        sortValue: get.toDateSortValue(node.connectionEnd),
      })

    row.push(
      {
        text: get.yearsAndMonths(connectionOverlapStart, connectionOverlapEnd, right),
        color,
        sortValue: get.yearsTenureSortValue(connectionOverlapDuration)
      })

    array.push(row)
  })

  if (array.length) {
    const headers = getConnectionsHeaders({ maxLseData, orderSet, tenseEnum })
    sortArray({ array, headers, orderSet, tableOrder, kind: KIND.NEXUS.PERSON.ROLES })
    return [headers, ...array]
  } else
    return null
}

const getConnectionsHeaders = ({ maxLseData, orderSet, tenseEnum }) => {
  const style = "tableHeader"
  const alignment = right
  const name = getHeader({ sortId: COLUMN.CODIRECTOR, prefix: `${TENSE[tenseEnum].NAME} `, style })
  const role = getHeader({ sortId: COLUMN.VIA_COMPANY, style })
  const index = getHeader({ sortId: COLUMN.LSE_INDEX, prefix: `${JARGON.LSE} `, style })
  const appointed = getHeader({ sortId: COLUMN.FROM, style, alignment })
  const resigned = getHeader({ sortId: COLUMN.UNTIL, style, alignment })
  const overlap = getHeader({ sortId: COLUMN.OVERLAP, style, alignment })

  const array = [name, role]

  if (maxLseData)
    array.push(index)

  array.push(appointed)

  return !!(orderSet & TABLE_BIT.RESIGNED)
    ? [...array, resigned, overlap]
    : [...array, overlap]
}
//#endregion connections table

const getIndexText = (indexBit, alignment) => ({
  text: get.index(indexBit),
  alignment,
})

export default cheatSheetPdf_PersonBoardRoles
